import type { PaginationMeta } from "@nasam/kernel";
import type {
  RfidConfig,
  SensorAssignmentListItem,
  SensorAssignmentsRepository,
  SensorConfig,
  SensorDetail,
  SensorFormMeta,
  SensorTargetScope,
  SensorType,
  WeightConfig,
} from "../domain";
import { isValueMapSensorType, sensorConfigIoNumbers } from "../domain";
import { sensorAssignmentListItemFixtures } from "../testing/sensorAssignmentFixtures";
import { sensorAssignmentListItemFromDto } from "./sensorAssignmentMappers";

/**
 * In-memory stand-in for {@link createSensorAssignmentsHttpRepository} while
 * `/sensors` is still `PLANNED` (no real backend yet). Serves the shared
 * fixtures for `list`, static bounds for `getForm`, `create`'s real
 * semantics (upsert, all-or-nothing, IO conflicts, `ALL`/`BRANCH` target
 * scopes resolved to fabricated device ids -- see resolveTargetDeviceIds),
 * and `getDeviceSensors` for the copy-existing-config picker, so the Assign
 * Sensor wizard behaves end to end. Swap the binding in
 * sensorAssignmentsFeature.ts back to the HTTP repository once the API ships.
 */
const MOCK_LATENCY_MS = 300;

function delay(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
}

function includes(haystack: string, needle: string): boolean {
  return haystack.toLocaleLowerCase().includes(needle.toLocaleLowerCase());
}

function matchesKeyword(row: SensorAssignmentListItem, keyword: string) {
  return (
    includes(row.name, keyword) ||
    includes(row.sensorType, keyword) ||
    includes(row.account.nameEn, keyword) ||
    includes(row.account.nameAr, keyword) ||
    includes(row.device.imei, keyword) ||
    includes(row.device.code, keyword)
  );
}

function pageMeta(
  page: number,
  limit: number,
  totalCount: number,
): PaginationMeta {
  const totalPages = Math.ceil(totalCount / limit);
  const pageUrl = (target: number) => `?page=${target}&limit=${limit}`;
  return {
    totalCount,
    currentPage: page,
    totalPages,
    firstPageUrl: pageUrl(1),
    lastPageUrl: pageUrl(Math.max(1, totalPages)),
    nextPageUrl: page < totalPages ? pageUrl(page + 1) : null,
    previousPageUrl: page > 1 ? pageUrl(page - 1) : null,
  };
}

// WEIGHT is the only sensor type with real bounds this phase -- every other
// type reports an empty, single-"DEFAULT"-variant form until its own step-2
// form ships (see SensorFormMeta).
function weightFormMeta(): SensorFormMeta {
  return {
    sensorType: "WEIGHT",
    variantKey: "formulaType",
    variants: ["NONE", "SUM", "PERCENT"],
    options: {},
    fields: [
      {
        key: "label",
        type: "STRING",
        labelEn: "Label",
        labelAr: "الاسم",
        requiredIn: ["NONE", "SUM", "PERCENT"],
        forbiddenIn: [],
        validation: { minLength: 1, maxLength: 100 },
        sortOrder: 1,
      },
      {
        key: "unitLabel",
        type: "STRING",
        labelEn: "Unit",
        labelAr: "وحدة القياس",
        requiredIn: ["NONE", "SUM", "PERCENT"],
        forbiddenIn: [],
        validation: { minLength: 1 },
        sortOrder: 2,
      },
      {
        key: "ios[].io",
        type: "INT",
        labelEn: "IO Number",
        labelAr: "رقم الإدخال/الإخراج",
        requiredIn: ["NONE", "SUM", "PERCENT"],
        forbiddenIn: [],
        validation: { min: 0, max: 65535 },
        sortOrder: 3,
      },
      {
        key: "ios[].minWeightKGs",
        type: "FLOAT",
        labelEn: "Min Weight (KGs)",
        labelAr: "الحد الأدنى للوزن (كجم)",
        requiredIn: ["NONE", "SUM", "PERCENT"],
        forbiddenIn: [],
        validation: { min: 0 },
        sortOrder: 4,
      },
      {
        key: "ios[].maxWeightKGs",
        type: "FLOAT",
        labelEn: "Max Weight (KGs)",
        labelAr: "الحد الأقصى للوزن (كجم)",
        requiredIn: ["NONE", "SUM", "PERCENT"],
        forbiddenIn: [],
        validation: {},
        sortOrder: 5,
      },
      {
        key: "ios[].label",
        type: "STRING",
        labelEn: "Label",
        labelAr: "الأسم",
        requiredIn: [],
        forbiddenIn: [],
        validation: { maxLength: 100 },
        sortOrder: 6,
      },
      {
        key: "ios[].ioFormulaPercentage",
        type: "FLOAT",
        labelEn: "Weight Percentage",
        labelAr: "نسبه الوزن",
        requiredIn: ["PERCENT"],
        forbiddenIn: ["NONE", "SUM"],
        validation: { min: 0, max: 100 },
        sortOrder: 7,
      },
      {
        key: "ios[].calibration[].mv",
        type: "FLOAT",
        labelEn: "Millivolt",
        labelAr: "مللي فولت",
        requiredIn: [],
        forbiddenIn: [],
        validation: {},
        sortOrder: 8,
      },
      {
        key: "ios[].calibration[].kg",
        type: "FLOAT",
        labelEn: "Kilograms",
        labelAr: "كيلوجرام",
        requiredIn: [],
        forbiddenIn: [],
        validation: {},
        sortOrder: 9,
      },
    ],
    rules: ["SENSOR_IO_DUPLICATED", "WEIGHT_PERCENTAGE_TOTAL"],
  };
}

// Matches the contract's own documented GET /sensors/form?sensorType=TEMPERATURE
// example: variantKey "ios[].probeType", options.beaconModel, per-channel
// requiredIn/forbiddenIn (BLE needs humidity+battery, forbids dallasId;
// DALLAS forbids battery, allows humidity/dallasId as optional).
function temperatureFormMeta(): SensorFormMeta {
  return {
    sensorType: "TEMPERATURE",
    variantKey: "ios[].probeType",
    variants: ["BLE", "DALLAS"],
    options: {
      beaconModel: [
        { value: "TZONE_BT04", labelEn: "Tzone BLE BT04", labelAr: "Tzone BLE BT04", sortOrder: 0 },
        { value: "MINEW_MST01", labelEn: "Minew MST01", labelAr: "Minew MST01", sortOrder: 1 },
      ],
      temperatureUnit: [{ value: "°C", labelEn: "Celsius", labelAr: "مئوية", sortOrder: 0 }],
    },
    fields: [
      {
        key: "label",
        type: "STRING",
        labelEn: "Label",
        labelAr: "الاسم",
        requiredIn: ["BLE", "DALLAS"],
        forbiddenIn: [],
        validation: { minLength: 1, maxLength: 100 },
        sortOrder: 1,
      },
      {
        key: "ios[].temperature.io",
        type: "INT",
        labelEn: "Temperature IO Number",
        labelAr: "رقم إدخال/إخراج درجة الحرارة",
        requiredIn: ["BLE", "DALLAS"],
        forbiddenIn: [],
        validation: { min: 0, max: 65535 },
        sortOrder: 2,
      },
      {
        key: "ios[].humidity.io",
        type: "INT",
        labelEn: "Humidity IO Number",
        labelAr: "رقم إدخال/إخراج الرطوبة",
        requiredIn: ["BLE"],
        forbiddenIn: [],
        validation: { min: 0, max: 65535 },
        sortOrder: 3,
      },
      {
        key: "ios[].battery.io",
        type: "INT",
        labelEn: "Battery IO Number",
        labelAr: "رقم إدخال/إخراج البطارية",
        requiredIn: ["BLE"],
        forbiddenIn: ["DALLAS"],
        validation: { min: 0, max: 65535 },
        sortOrder: 4,
      },
      {
        key: "ios[].dallasId.io",
        type: "INT",
        labelEn: "DALLAS ID IO Number",
        labelAr: "رقم إدخال/إخراج DALLAS-ID",
        requiredIn: [],
        forbiddenIn: ["BLE"],
        validation: { min: 0, max: 65535 },
        sortOrder: 5,
      },
      {
        key: "ios[].beaconModel",
        type: "SELECT",
        labelEn: "Multiplier",
        labelAr: "معامل الضرب",
        requiredIn: ["BLE", "DALLAS"],
        forbiddenIn: [],
        optionGroup: "beaconModel",
        validation: {},
        sortOrder: 6,
      },
    ],
    rules: ["SENSOR_IO_DUPLICATED"],
  };
}

// Shared by every SimpleIoSensorType -- one field, the IO number.
function simpleIoFormMeta(sensorType: SensorType): SensorFormMeta {
  return {
    sensorType,
    variantKey: null,
    variants: ["DEFAULT"],
    options: {},
    fields: [
      {
        key: "ios[].io",
        type: "INT",
        labelEn: "IO Number",
        labelAr: "رقم الإدخال/الإخراج",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        validation: { min: 0, max: 65535 },
        sortOrder: 1,
      },
    ],
    rules: ["SENSOR_IO_DUPLICATED"],
  };
}

function rfidFormMeta(): SensorFormMeta {
  return {
    sensorType: "RFID",
    variantKey: null,
    variants: ["DEFAULT"],
    options: {},
    fields: [
      {
        key: "ios[].io",
        type: "INT",
        labelEn: "IO Number",
        labelAr: "رقم الإدخال/الإخراج",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        validation: { min: 0, max: 65535 },
        sortOrder: 1,
      },
      {
        key: "ios[].tagLength",
        type: "INT",
        labelEn: "Tag Verification Length",
        labelAr: "طول علامة التحقق",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        // Confirmed with BE (2026-09-24): max 64.
        validation: { min: 1, max: 64 },
        sortOrder: 2,
      },
    ],
    rules: ["SENSOR_IO_DUPLICATED"],
  };
}

// Shared by every ValueMapSensorType (DOOR_STATUS, PTO, SEAT_BELT,
// REFRIGERATOR_STATE, HEADLIGHTS, SOS) -- confirmed real (2026-09-23,
// GET /sensors/form?sensorType=...), identical field list across all six.
// Each `ios[]` entry needs its own label plus exactly two value mappings;
// the live response also carries `groups: [{ key: "ios[].values[]",
// minItems: 2, maxItems: 2 }]`, not modeled on SensorFormMeta since
// ValueMapSensorConfigStep hardcodes the two value rows itself rather than
// reading `groups`.
function valueMapFormMeta(sensorType: SensorType): SensorFormMeta {
  return {
    sensorType,
    variantKey: null,
    variants: ["DEFAULT"],
    options: {},
    fields: [
      {
        key: "label",
        type: "STRING",
        labelEn: "Name",
        labelAr: "الاسم",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        validation: { minLength: 1, maxLength: 100 },
        sortOrder: 0,
      },
      {
        key: "ios[].label",
        type: "STRING",
        labelEn: "Label",
        labelAr: "التسمية",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        validation: { minLength: 1, maxLength: 100 },
        sortOrder: 1,
      },
      {
        key: "ios[].io",
        type: "INT",
        labelEn: "IO Number",
        labelAr: "رقم IO",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        validation: { min: 0, max: 65535 },
        sortOrder: 2,
      },
      {
        key: "ios[].values[].value",
        type: "DECIMAL",
        labelEn: "Value",
        labelAr: "القيمة",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        validation: {},
        sortOrder: 3,
      },
      {
        key: "ios[].values[].label.en",
        type: "STRING",
        labelEn: "Label (English)",
        labelAr: "الاسم (بالإنجليزية)",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        validation: { minLength: 1, maxLength: 100 },
        sortOrder: 4,
      },
      {
        key: "ios[].values[].label.ar",
        type: "STRING",
        labelEn: "Label (Arabic)",
        labelAr: "الاسم (بالعربية)",
        requiredIn: ["DEFAULT"],
        forbiddenIn: [],
        validation: { minLength: 1, maxLength: 100 },
        sortOrder: 5,
      },
    ],
    rules: ["SENSOR_VALUES_DUPLICATED"],
  };
}

function weightDerivedFields(config: WeightConfig) {
  const mins = config.ios.map((io) => io.minWeightKGs);
  const maxs = config.ios.map((io) => io.maxWeightKGs);
  return {
    minValue: mins.length > 0 ? Math.min(...mins) : null,
    maxValue: maxs.length > 0 ? Math.max(...maxs) : null,
    unit: config.unitLabel,
  };
}

// RFID readers on one device share the same tag format in practice -- the
// list row's `tagLength` is just a representative value, not a summary
// across (possibly differing) rows.
function rfidDerivedTagLength(config: RfidConfig): number | null {
  return config.ios[0]?.tagLength ?? null;
}

export function createSensorAssignmentsMockRepository(): SensorAssignmentsRepository {
  let rows: readonly SensorAssignmentListItem[] =
    sensorAssignmentListItemFixtures.map(sensorAssignmentListItemFromDto);
  let nextSensorId = rows.length + 1;
  let nextConfigId = rows.length + 1;

  // deviceId -> io -> the sensor row currently holding it. Only tracks IOs
  // assigned through `create()` in this session -- the seeded fixtures don't
  // carry a real WeightConfig, so they can't be conflict-checked against;
  // build a conflict scenario by calling `create()` twice against the same
  // device/IO within a test.
  const deviceIoMap = new Map<string, Map<number, string>>();

  // sensorConfigId -> the full config, so `getDeviceSensors` (the "copy
  // existing configuration" picker) can read it back. Only populated for
  // configs created through `create()` in this session -- same limitation
  // as deviceIoMap above.
  const configsById = new Map<string, SensorConfig>();

  // `ALL`/`BRANCH` scopes have no real resolution mechanism yet (see
  // SensorTargetScope) -- fabricate a plausible device id set so `create`
  // still has something to upsert against for local testing.
  function resolveTargetDeviceIds(target: SensorTargetScope): string[] {
    if (target.type === "DEVICES") return [...target.deviceIds];
    if (target.type === "ALL") {
      const known = rows
        .filter((row) => row.account.id === target.accountId)
        .map((row) => row.device.id);
      const unique = [...new Set(known)];
      return unique.length > 0
        ? unique
        : [`${target.accountId}-all-1`, `${target.accountId}-all-2`];
    }
    return [`${target.branchId}-branch-1`, `${target.branchId}-branch-2`];
  }

  function deviceRefFor(deviceId: string) {
    const known = rows.find((row) => row.device.id === deviceId);
    return known?.device ?? { id: deviceId, code: deviceId, imei: deviceId };
  }

  function accountRefFor(deviceId: string) {
    const known = rows.find((row) => row.device.id === deviceId);
    return known?.account ?? sensorAssignmentListItemFixtures[0]!.account;
  }

  function recomputeDeviceCount(sensorConfigId: string) {
    const count = rows.filter(
      (row) => row.sensorConfig.id === sensorConfigId,
    ).length;
    rows = rows.map((row) =>
      row.sensorConfig.id === sensorConfigId
        ? { ...row, sensorConfig: { ...row.sensorConfig, deviceCount: count } }
        : row,
    );
  }

  function toDetail(row: SensorAssignmentListItem): SensorDetail {
    return {
      ...row,
      config: configsById.get(row.sensorConfig.id) ?? null,
      updatedAt: new Date().toISOString(),
    };
  }

  function upsert(
    deviceIds: readonly string[],
    sensorType: SensorType,
    sensorConfigId: string,
    fields: Pick<
      SensorAssignmentListItem,
      "name" | "configType" | "minValue" | "maxValue" | "unit" | "tagLength"
    >,
  ): { created: SensorDetail[]; updated: SensorDetail[] } {
    const created: SensorDetail[] = [];
    const updated: SensorDetail[] = [];

    for (const deviceId of deviceIds) {
      const existingIndex = rows.findIndex(
        (row) => row.device.id === deviceId && row.sensorType === sensorType,
      );
      if (existingIndex === -1) {
        const id = `sensor-${String(nextSensorId++)}`;
        const row: SensorAssignmentListItem = {
          id,
          sensorType,
          ...fields,
          sensorConfig: { id: sensorConfigId, deviceCount: 0 },
          device: deviceRefFor(deviceId),
          account: accountRefFor(deviceId),
          createdAt: new Date().toISOString(),
        };
        rows = [...rows, row];
        created.push(toDetail(row));
      } else {
        const previous = rows[existingIndex]!;
        const next: SensorAssignmentListItem = {
          ...previous,
          ...fields,
          sensorConfig: { id: sensorConfigId, deviceCount: 0 },
        };
        rows = rows.map((row, index) => (index === existingIndex ? next : row));
        updated.push(toDetail(next));
      }
    }

    recomputeDeviceCount(sensorConfigId);
    return { created, updated };
  }

  return {
    async list(query = {}) {
      await delay();

      const page = query.page ?? 1;
      const limit = query.limit ?? 10;
      const keyword = query.search?.trim();
      const from =
        query.createdFrom === undefined
          ? undefined
          : Date.parse(query.createdFrom);
      const to =
        query.createdTo === undefined ? undefined : Date.parse(query.createdTo);
      // The API defaults to desc; only "asc" opts out of it.
      const direction = query.sort === "asc" ? 1 : -1;
      const sortBy = query.sortBy ?? "createdAt";

      const filtered = rows
        .filter(
          (row) =>
            (!keyword || matchesKeyword(row, keyword)) &&
            (query.sensorType === undefined ||
              row.sensorType === query.sensorType) &&
            (query.accountId === undefined ||
              row.account.id === query.accountId) &&
            (query.deviceId === undefined ||
              row.device.id === query.deviceId) &&
            (from === undefined || Date.parse(row.createdAt) >= from) &&
            (to === undefined || Date.parse(row.createdAt) <= to),
        )
        .toSorted((a, b) =>
          sortBy === "sensorType"
            ? a.sensorType.localeCompare(b.sensorType) * direction
            : (Date.parse(a.createdAt) - Date.parse(b.createdAt)) * direction,
        );

      return {
        data: filtered.slice((page - 1) * limit, page * limit),
        meta: pageMeta(page, limit, filtered.length),
      };
    },

    async getForm(sensorType) {
      await delay();
      if (sensorType === "WEIGHT") return weightFormMeta();
      if (sensorType === "TEMPERATURE") return temperatureFormMeta();
      if (sensorType === "RFID") return rfidFormMeta();
      if (isValueMapSensorType(sensorType)) return valueMapFormMeta(sensorType);
      return simpleIoFormMeta(sensorType);
    },

    async create(input) {
      await delay();
      const deviceIds = resolveTargetDeviceIds(input.target);

      // Copy path: repoint the named devices at the existing configuration,
      // no re-validation (its IOs were already valid when first created).
      if ("sensorConfigId" in input) {
        const existing = rows.find(
          (row) => row.sensorConfig.id === input.sensorConfigId,
        );
        if (existing === undefined) {
          throw new Error(`Unknown sensorConfigId: ${input.sensorConfigId}`);
        }
        const { created, updated } = upsert(
          deviceIds,
          existing.sensorType,
          input.sensorConfigId,
          {
            name: existing.name,
            configType: existing.configType,
            minValue: existing.minValue,
            maxValue: existing.maxValue,
            unit: existing.unit,
            tagLength: existing.tagLength,
          },
        );
        return { status: "created", sensorConfigId: input.sensorConfigId, created, updated };
      }

      const { sensorType, config } = input;
      const ioNumbers = sensorConfigIoNumbers(sensorType, config);

      // IO conflicts -- checked across every named device before anything is
      // written, so the write stays all-or-nothing.
      const conflicts: { deviceId: string; imei: string; io: number }[] = [];
      for (const deviceId of deviceIds) {
        const existingIos = deviceIoMap.get(deviceId);
        if (existingIos === undefined) continue;
        // A device repointing its own sensor of this type doesn't conflict
        // with the IOs that sensor itself already holds.
        const ownRow = rows.find(
          (row) => row.device.id === deviceId && row.sensorType === sensorType,
        );
        for (const io of ioNumbers) {
          const holder = existingIos.get(io);
          if (holder !== undefined && holder !== ownRow?.id) {
            conflicts.push({
              deviceId,
              imei: deviceRefFor(deviceId).imei,
              io,
            });
          }
        }
      }
      if (conflicts.length > 0) {
        return {
          status: "io-conflict",
          message:
            "This sensor has already been assigned to the selected device.",
          conflicts,
        };
      }

      const sensorConfigId = `sensor-config-${String(nextConfigId++)}`;
      const derived =
        sensorType === "WEIGHT"
          ? weightDerivedFields(config)
          : { minValue: null, maxValue: null, unit: null };
      configsById.set(sensorConfigId, config);

      const { created, updated } = upsert(deviceIds, sensorType, sensorConfigId, {
        name: config.label,
        configType: sensorType === "WEIGHT" ? config.formulaType : null,
        minValue: derived.minValue,
        maxValue: derived.maxValue,
        unit: derived.unit,
        tagLength: sensorType === "RFID" ? rfidDerivedTagLength(config) : null,
      });

      for (const deviceId of deviceIds) {
        let map = deviceIoMap.get(deviceId);
        if (map === undefined) {
          map = new Map();
          deviceIoMap.set(deviceId, map);
        }
        const holderId =
          created.find((item) => item.device.id === deviceId)?.id ??
          updated.find((item) => item.device.id === deviceId)?.id;
        if (holderId === undefined) continue;
        for (const io of ioNumbers) map.set(io, holderId);
      }

      return { status: "created", sensorConfigId, created, updated };
    },

    async getById(sensorId) {
      await delay();
      const row = rows.find((candidate) => candidate.id === sensorId);
      if (row === undefined) throw new Error(`Unknown sensorId: ${sensorId}`);
      return toDetail(row);
    },

    async update(sensorId, input) {
      await delay();
      const index = rows.findIndex((row) => row.id === sensorId);
      if (index === -1) throw new Error(`Unknown sensorId: ${sensorId}`);
      const previous = rows[index]!;
      const { sensorType, config } = input;
      const ioNumbers = sensorConfigIoNumbers(sensorType, config);

      // Same IO clash rule as create(), excluding this sensor's own IOs.
      const existingIos = deviceIoMap.get(previous.device.id);
      const conflicts: { deviceId: string; imei: string; io: number }[] = [];
      if (existingIos !== undefined) {
        for (const io of ioNumbers) {
          const holder = existingIos.get(io);
          if (holder !== undefined && holder !== previous.id) {
            conflicts.push({ deviceId: previous.device.id, imei: previous.device.imei, io });
          }
        }
      }
      if (conflicts.length > 0) {
        return {
          status: "io-conflict",
          message: "This sensor has already been assigned to the selected device.",
          conflicts,
        };
      }

      const sensorConfigId = `sensor-config-${String(nextConfigId++)}`;
      const previousConfigId = previous.sensorConfig.id;
      configsById.set(sensorConfigId, config);

      const derived =
        sensorType === "WEIGHT"
          ? weightDerivedFields(config)
          : { minValue: null, maxValue: null, unit: null };

      const next: SensorAssignmentListItem = {
        ...previous,
        name: config.label,
        configType: sensorType === "WEIGHT" ? config.formulaType : null,
        minValue: derived.minValue,
        maxValue: derived.maxValue,
        unit: derived.unit,
        tagLength: sensorType === "RFID" ? rfidDerivedTagLength(config) : null,
        sensorConfig: { id: sensorConfigId, deviceCount: 0 },
      };
      rows = rows.map((row, rowIndex) => (rowIndex === index ? next : row));
      recomputeDeviceCount(sensorConfigId);
      recomputeDeviceCount(previousConfigId); // retires the old config if this was its last device.

      let map = deviceIoMap.get(previous.device.id);
      if (map === undefined) {
        map = new Map();
        deviceIoMap.set(previous.device.id, map);
      }
      for (const io of ioNumbers) map.set(io, previous.id);

      return { status: "updated", sensor: toDetail(next) };
    },

    async delete(sensorId) {
      await delay();
      const row = rows.find((candidate) => candidate.id === sensorId);
      if (row === undefined) throw new Error(`Unknown sensorId: ${sensorId}`);
      rows = rows.filter((candidate) => candidate.id !== sensorId);
      // Retires the configuration (invisibly) if this was its last device.
      recomputeDeviceCount(row.sensorConfig.id);
      return { responseMessage: "Sensor assignment removed successfully." };
    },

    async getDeviceSensors(deviceId) {
      await delay();
      return rows.filter((row) => row.device.id === deviceId).map(toDetail);
    },
  };
}
